/**
 * User Service
 * API calls for user management
 */

import apiService from './api.service';

export interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: 'tenant_user';
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  isActive?: boolean;
  password?: string;
}

export interface UserListParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface UserListResponse {
  data: User[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class UserService {
  /**
   * Get paginated list of users
   */
  async getUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const response = await apiService.get<{ success: boolean; data: User[]; meta: any }>(
      '/admin/users',
      { params }
    );
    return {
      data: response.data || [],
      meta: response.meta || { total: 0, page: 1, limit: 20, totalPages: 0 },
    };
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<User> {
    const response = await apiService.get<{ success: boolean; data: User }>(
      `/admin/users/${userId}`
    );
    return response.data;
  }

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserData): Promise<{ message: string; user: User }> {
    const response = await apiService.post<{
      success: boolean;
      data: { message: string; user: User };
    }>('/admin/users', userData);
    return response.data;
  }

  /**
   * Update user
   */
  async updateUser(
    userId: string,
    userData: UpdateUserData
  ): Promise<{ message: string; user: User }> {
    const response = await apiService.put<{
      success: boolean;
      data: { message: string; user: User };
    }>(`/admin/users/${userId}`, userData);
    return response.data;
  }

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await apiService.delete<{ success: boolean; data: { message: string } }>(
      `/admin/users/${userId}`
    );
    return response.data;
  }
}

export default new UserService();
